// src/stores/useRoutingStore.js
//
// Origin, destination, and the route between them. RAW METRES THROUGHOUT —
// nothing here rounds. Rounding is a render concern and lives in
// lib/routeFormat.js, so the cached response stays byte-faithful to what the
// API returned and a later change to display units cannot corrupt stored data.
//
// ── THE CACHE, AND WHY IT IS SHAPED THIS WAY ────────────────────────────────
//
// This view is used in the field, on a Starlink link that drops. The cache
// exists for one situation: the person already has the route on screen, the
// link goes, and they still need it.
//
//   ONE ENTRY, NOT A HISTORY. The last successful route only — its request
//   parameters, the full response, and fetched_at. A history invites showing
//   the wrong journey, and the wrong journey is worse than no journey.
//
//   EXACT PARAMETER MATCH OR NO HIT. A cached route surfaces only when the
//   current request is byte-identical to the one that produced it. Different
//   origin, different destination, different pad — no hit, and the person sees
//   the offline refusal instead. Showing a route for a DIFFERENT journey
//   because it was the nearest thing available is the failure this rule exists
//   to prevent.
//
//   NO TTL. A cached route ages and does not expire. Nothing about a road
//   traced from imagery becomes false after an hour, and a cache that silently
//   emptied itself at the moment the link dropped would be useless exactly when
//   it is needed. Age is SURFACED and the person decides.
//
//   ONLY A 200 REPLACES IT. A failed request — refusal or network — leaves the
//   cache untouched. A 503 must not evict the route someone is standing in
//   front of.
//
// ── PERSIST KEY ─────────────────────────────────────────────────────────────
//
// `aiprime.routing.v1`, classified USER-SCOPED in auth/persistKeys.js. The
// `aiprime.` prefix puts it under sessionGuard's sweep, so it clears on a
// different-user login like everything else. It is user-scoped rather than
// device-scoped because a route to a pad is a statement about someone's
// entitled acreage, and the next person on a shared tablet has no business
// inheriting it.

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { PERSIST_KEYS } from "../auth/persistKeys";

const ENDPOINT = "/api/v1/routing/route";

/**
 * Canonical request parameters. Also the cache key.
 *
 * Built from the same values the request body is built from, so "the cache
 * matches this request" and "the cache matches what we sent" cannot drift.
 * Returns null when the request is not yet complete.
 */
export function requestParamsOf(origin, destination) {
  if (!origin || !Number.isFinite(origin.lng) || !Number.isFinite(origin.lat)) return null;
  if (!destination) return null;

  const from = { lng: origin.lng, lat: origin.lat };

  if (destination.padObservationId) {
    return { from, padObservationId: destination.padObservationId };
  }
  if (Number.isFinite(destination.lng) && Number.isFinite(destination.lat)) {
    return { from, to: { lng: destination.lng, lat: destination.lat } };
  }
  return null;
}

/** Exact match. Not "close enough" — see the header. */
export function sameParams(a, b) {
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

// Every way this request can fail, as a closed set. The view renders copy per
// `kind`; nothing falls through to a generic message, because a refusal a
// person cannot act on is the same as a silent failure.
export const REFUSAL = {
  BAD_REQUEST:   "bad_request",     // 400
  UNAUTHORIZED:  "unauthorized",    // 401
  NOT_ENTITLED:  "not_entitled",    // 403
  PAD_NOT_FOUND: "pad_not_found",   // 404
  DISCONNECTED:  "disconnected",    // 422 reason=disconnected_components
  SNAP_TOO_FAR:  "snap_too_far",    // 422 reason=snap_too_far
  NO_PATH:       "no_path",         // 422 reason=no_path
  NO_BUILD:      "no_build",        // 503
  OFFLINE_STALE: "offline_stale",   // network failure, matching cache shown
  OFFLINE:       "offline",         // network failure, nothing cached to show
  SERVER_ERROR:  "server_error",    // 500 and anything unclassified
};

function classify(status, body) {
  switch (status) {
    case 400: return REFUSAL.BAD_REQUEST;
    case 401: return REFUSAL.UNAUTHORIZED;
    case 403: return REFUSAL.NOT_ENTITLED;
    case 404: return REFUSAL.PAD_NOT_FOUND;
    case 503: return REFUSAL.NO_BUILD;
    case 422:
      if (body?.reason === "disconnected_components") return REFUSAL.DISCONNECTED;
      if (body?.reason === "snap_too_far") return REFUSAL.SNAP_TOO_FAR;
      if (body?.reason === "no_path") return REFUSAL.NO_PATH;
      return REFUSAL.SERVER_ERROR;
    default:
      return REFUSAL.SERVER_ERROR;
  }
}

const EMPTY = {
  origin: null,
  destination: null,
  route: null,
  loading: false,
  error: null,
  activeStepIndex: null,
  isStale: false,
  staleFetchedAt: null,
};

// Sticky for the session, and deliberately NOT persisted. A 403 is a fact about
// the ORG's grants, which an administrator can change while the tab is open, so
// remembering it across reloads would outlive the thing it describes. Within one
// session it is enough to stop sending someone into the same dead end twice —
// which is all the shell needs, because entitlement is not otherwise knowable
// before the request (no field on /auth/me, and the only endpoint that exposes
// grants is platform-admin only).

export const useRoutingStore = create(
  persist(
    (set, get) => ({
      ...EMPTY,
      // { params, response, fetched_at } — the whole cache.
      cached: null,
      notEntitled: false,

      setOrigin: (origin) =>
        set({ origin, route: null, error: null, activeStepIndex: null, isStale: false }),

      setDestination: (destination) =>
        set({ destination, route: null, error: null, activeStepIndex: null, isStale: false }),

      setActiveStep: (activeStepIndex) => set({ activeStepIndex }),

      // notEntitled deliberately survives clear(): clearing a route is a
      // statement about this journey, not about the org's licence.
      clear: () => set({ ...EMPTY }),

      /** Steps of the current route, or []. Never throws on a partial response. */
      steps: () => get().route?.routes?.[0]?.legs?.[0]?.steps ?? [],

      async requestRoute() {
        const { origin, destination, cached } = get();
        const params = requestParamsOf(origin, destination);
        if (!params) return;

        set({ loading: true, error: null, isStale: false, activeStepIndex: null });

        let res;
        try {
          res = await fetchWithAuth(ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(params),
          });
        } catch {
          // Transport failed, or fetchWithAuth refused because the session is
          // gone. Either way no status exists. The cache is NOT touched.
          const hit = cached && sameParams(cached.params, params);
          set({
            loading: false,
            route: hit ? cached.response : null,
            isStale: !!hit,
            staleFetchedAt: hit ? cached.fetched_at : null,
            error: { kind: hit ? REFUSAL.OFFLINE_STALE : REFUSAL.OFFLINE },
          });
          return;
        }

        let body = null;
        try { body = await res.json(); } catch { /* refusals always carry JSON; a body that will not parse is still a status */ }

        if (!res.ok) {
          const kind = classify(res.status, body);
          set({
            loading: false,
            route: null,
            isStale: false,
            staleFetchedAt: null,
            error: { kind, status: res.status, body },
            ...(kind === REFUSAL.NOT_ENTITLED ? { notEntitled: true } : {}),
          });
          return;
        }

        // 200. The only thing that replaces the cache.
        set({
          loading: false,
          route: body,
          error: null,
          isStale: false,
          staleFetchedAt: null,
          cached: { params, response: body, fetched_at: Date.now() },
          // A 200 is proof of entitlement. If a grant was added while the tab
          // was open, the flag must not outlive it.
          notEntitled: false,
        });
      },
    }),
    {
      name: PERSIST_KEYS.routing,
      // The cache and nothing else. Persisting `loading` would rehydrate a
      // spinner for a request that is not running; persisting `error` would
      // rehydrate a refusal for a request nobody made.
      partialize: (s) => ({ cached: s.cached }),
    }
  )
);
